export { B as BaseTheme, G as GreyPalette } from './base-57e9514e.js';
export { darken, getThemeProp, lighten } from './helpers.js';
import { css, withTheme } from 'styled-components';
import { useState, useEffect } from 'react';
export { c as A2bTheme, b as ATOMTheme, A as AbacusTheme, a as AdvisorConsoleTheme, B as BounceNextTheme, C as CavaTheme, d as CertifyTheme, e as ChromeRiverTheme, D as DynamicThemeProvider, E as EATheme, f as EdenredPayTheme, g as EmburseTheme, L as LassoTheme, N as NexoniaTheme, i as SVBTheme, S as SpendTheme, h as SutisoftTheme, T as TallieTheme, t as themeNameLookup } from './withThemeProvider-1ef7b57b.js';
import '@amtrav/icons';
import 'tinycolor2';
import 'ramda';

/* see note in @amtrav/themes/MatchMedia why we are using 0.02 in the media query */
const stepDifferential$1 = 0.02;

/**
 * Used with styled-components
 */
var media = {
  XSmallOnly: function () {
    return css`
    @media (max-width: ${_ref => {
      let {
        theme
      } = _ref;
      return theme.global['breakpointSM--size'] - stepDifferential$1;
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  Small: function () {
    return css`
    @media (min-width: ${_ref2 => {
      let {
        theme
      } = _ref2;
      return theme.global['breakpointSM--size'];
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  SmallOnly: function () {
    return css`
    @media (min-width: ${_ref3 => {
      let {
        theme
      } = _ref3;
      return theme.global['breakpointSM--size'];
    }}px) and (max-width: ${_ref4 => {
      let {
        theme
      } = _ref4;
      return theme.global['breakpointMD--size'] - stepDifferential$1;
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  Medium: function () {
    return css`
    @media (min-width: ${_ref5 => {
      let {
        theme
      } = _ref5;
      return theme.global['breakpointMD--size'];
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  MediumOnly: function () {
    return css`
    @media (min-width: ${_ref6 => {
      let {
        theme
      } = _ref6;
      return theme.global['breakpointMD--size'];
    }}px) and (max-width: ${_ref7 => {
      let {
        theme
      } = _ref7;
      return theme.global['breakpointLG--size'] - stepDifferential$1;
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  Large: function () {
    return css`
    @media (min-width: ${_ref8 => {
      let {
        theme
      } = _ref8;
      return theme.global['breakpointLG--size'];
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  LargeOnly: function () {
    return css`
    @media (min-width: ${_ref9 => {
      let {
        theme
      } = _ref9;
      return theme.global['breakpointLG--size'];
    }}px) and (max-width: ${_ref10 => {
      let {
        theme
      } = _ref10;
      return theme.global['breakpointXL--size'] - stepDifferential$1;
    }}px) {
      ${css(...arguments)};
    }
  `;
  },
  XLargeOnly: function () {
    return css`
    @media (min-width: ${_ref11 => {
      let {
        theme
      } = _ref11;
      return theme.global['breakpointXL--size'];
    }}px) {
      ${css(...arguments)};
    }
  `;
  }
};

function useMediaQuery(mediaQuery) {
  const [query, setQuery] = useState(mediaQuery);
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (!query) return () => {};
    const onChangeEvt = mql => {
      setMatches(mql.matches);
    };
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    // listen for media query changes
    mql.addEventListener('change', onChangeEvt);
    return () => {
      mql.removeEventListener('change', onChangeEvt);
    };
  }, [query]);
  return [matches, setQuery];
}

/* eslint-disable react/prop-types */
/*
Normally we'd want our media queries to always step up or down at whole pixel increments. However, we ran into an edge
case where in Windows scaling up the OS by 150% caused media queries to fail to match in the boundary conditions. These
media queries failed to match because the browser was not resizing at whole pixel increments but instead at viewports 
with fractional widths. The fix was to shift the media queries by 0.02px at these boundaries.

More details: 
https://www.w3.org/TR/mediaqueries-4/#mq-min-max
https://stackoverflow.com/questions/51566916/why-does-bootstrap-use-a-0-02px-difference-between-screen-size-thresholds-in-its#answer-51567792
*/
const stepDifferential = 0.02;
const XSmallOnly = _ref => {
  let {
    children,
    theme
  } = _ref;
  return useMediaQuery(`(max-width: ${theme.global['breakpointSM--size'] - stepDifferential}px)`)[0] ? children : null;
};
const Small = _ref2 => {
  let {
    children,
    theme
  } = _ref2;
  return useMediaQuery(`(min-width: ${theme.global['breakpointSM--size']}px)`)[0] ? children : null;
};
const SmallOnly = _ref3 => {
  let {
    children,
    theme
  } = _ref3;
  return useMediaQuery(`(min-width: ${theme.global['breakpointSM--size']}px) and (max-width: ${theme.global['breakpointMD--size'] - stepDifferential}px)`)[0] ? children : null;
};
const SmallAndLess = _ref4 => {
  let {
    children,
    theme
  } = _ref4;
  return useMediaQuery(`(max-width: ${theme.global['breakpointMD--size'] - stepDifferential}px)`)[0] ? children : null;
};
const Medium = _ref5 => {
  let {
    children,
    theme
  } = _ref5;
  return useMediaQuery(`(min-width: ${theme.global['breakpointMD--size']}px)`)[0] ? children : null;
};
const MediumOnly = _ref6 => {
  let {
    children,
    theme
  } = _ref6;
  return useMediaQuery(`(min-width: ${theme.global['breakpointMD--size']}px) and (max-width: ${theme.global['breakpointLG--size'] - stepDifferential}px)`)[0] ? children : null;
};
const MediumAndLess = _ref7 => {
  let {
    children,
    theme
  } = _ref7;
  return useMediaQuery(`(max-width: ${theme.global['breakpointLG--size'] - stepDifferential}px)`)[0] ? children : null;
};
const Large = _ref8 => {
  let {
    children,
    theme
  } = _ref8;
  return useMediaQuery(`(min-width: ${theme.global['breakpointLG--size']}px)`)[0] ? children : null;
};
const LargeOnly = _ref9 => {
  let {
    children,
    theme
  } = _ref9;
  return useMediaQuery(`(min-width: ${theme.global['breakpointLG--size']}px) and (max-width: ${theme.global['breakpointXL--size'] - stepDifferential}px)`)[0] ? children : null;
};
const LargeAndLess = _ref10 => {
  let {
    children,
    theme
  } = _ref10;
  return useMediaQuery(`(max-width: ${theme.global['breakpointXL--size'] - stepDifferential}px)`)[0] ? children : null;
};
const XLargeOnly = _ref11 => {
  let {
    children,
    theme
  } = _ref11;
  return useMediaQuery(`(min-width: ${theme.global['breakpointXL--size']}px)`)[0] ? children : null;
};
function useCoarseInput() {
  return useMediaQuery(`(pointer: coarse)`)[0];
}
function useFineInput() {
  return useMediaQuery(`(pointer: fine)`)[0];
}
const CoarseInput = _ref12 => {
  let {
    children
  } = _ref12;
  return useCoarseInput() ? children : null;
};
const FineInput = _ref13 => {
  let {
    children
  } = _ref13;
  return useFineInput() ? children : null;
};
const MatchMedia = {};
MatchMedia.XSmallOnly = withTheme(XSmallOnly);
MatchMedia.Small = withTheme(Small);
MatchMedia.SmallOnly = withTheme(SmallOnly);
MatchMedia.SmallAndLess = withTheme(SmallAndLess);
MatchMedia.Medium = withTheme(Medium);
MatchMedia.MediumOnly = withTheme(MediumOnly);
MatchMedia.MediumAndLess = withTheme(MediumAndLess);
MatchMedia.Large = withTheme(Large);
MatchMedia.LargeOnly = withTheme(LargeOnly);
MatchMedia.LargeAndLess = withTheme(LargeAndLess);
MatchMedia.XLargeOnly = withTheme(XLargeOnly);

/**
 * Targets devices with limited accuracy, such as a finger on a touchscreen.
 */
MatchMedia.CoarseInput = withTheme(CoarseInput);
/**
 * Targets devices with an accurate pointing device, such as a mouse.
 */
MatchMedia.FineInput = withTheme(FineInput);

// returns a boolean
function isMatchedMaxWidth() {
  let width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '768px';
  return useMediaQuery(`(max-width: ${width})`)[0];
}

// returns a boolean
function isMatchedMinWidth() {
  let width = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '768px';
  return useMediaQuery(`(min-width: ${width})`)[0];
}

// https://getbootstrap.com/docs/4.0/layout/overview/#z-index
const zIndex = {
  DATE_RANGE: 500,
  DATE_RANGE_MASK: 501,
  DATE_SELECTED: 502,
  DATE_HOVER: 503,
  DATE_NUMBER: 504,
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  OVERLAY: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070
};

export { MatchMedia, zIndex as ZINDEX, isMatchedMaxWidth, isMatchedMinWidth, media, useCoarseInput, useFineInput };
//# sourceMappingURL=index.js.map
